import { useEffect, useRef, useState } from 'react';
import useTranscripts from 'api/hooks/useTranscripts';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { useSearchParams } from 'react-router';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import filterTranscriptsByCategory, {
  groupTranscriptsByCategory,
} from 'components/Dashboard/Transcripts/filterTranscriptsByCategory';
import time from 'utils/time';

// Chamber debates (Commons/Lords/Westminster Hall) are all Hansard content, hence one icon;
// Committees are a distinct icon already used elsewhere for committee membership.
const SECTION_ICONS = {
  Commons: 'parliamentary-record',
  Lords: 'parliamentary-record',
  Committees: 'group',
  'Westminster Hall': 'parliamentary-record',
  Other: 'default',
};

const CreateTranscripts = (props) => {
  const { openState, activeDebateState, activeSectionState, dateState } = props;
  const { setIsOpen } = openState;
  const { setActiveDebate } = activeDebateState;
  const { activeSection } = activeSectionState;
  const { date } = dateState;
  const [isLoading, setIsLoading] = useState(false);

  const [events, setEvents] = useState([]);
  const [debates, setDebates] = useState([]);

  const { getTranscriptsEvents, getTranscriptsDebates } = useTranscripts();
  const [searchParams, setSearchParams] = useSearchParams();
  const [heightContainer, containerRef] = useHeightContainer();

  const initialLoad = useRef(null);
  initialLoad.current = async (signal) => {
    try {
      setIsLoading(true);

      const sortParam = getUrlParam('sort');
      const next = new URLSearchParams(searchParams);
      if (!!sortParam) {
        next.delete('sort');
      }
      setSearchParams(next);
      const [eventsData, debatesData] = await Promise.all([
        getTranscriptsEvents({ signal, isoDate: date }),
        getTranscriptsDebates({ signal, isoDate: date }),
      ]);
      if (!!eventsData || !!debatesData) {
        setEvents(eventsData ?? []);
        setDebates(debatesData ?? []);
        setIsLoading(false);
      }
    } catch (error) {}
  };

  useEffect(() => {
    const controller = new AbortController();
    initialLoad.current(controller.signal);
    return () => {
      controller.abort('Transcripts page cancelled by the user.');
    };
  }, [date]);

  const filteredItems = filterTranscriptsByCategory([...events, ...debates], activeSection);
  const groupedItems = groupTranscriptsByCategory(filteredItems);

  return (
    <div ref={containerRef}>
      <CustomScrollbar
        className={`main-content-scrollbar`}
        style={{ height: `${heightContainer}px` }}
        maximalThumbYSize={100}
      >
        <div className='p-4 pt-lg-5 px-lg-5 pb-2'>
          <p className='mb-0 title-h5-m-bold'>
            Choose an event below to create a transcript and tailored report for it
          </p>
        </div>
        <div className='px-4 px-lg-5 pb-4'>
          <div
            className='bg-light-blue-hovered border-general border-grey-50 rounded p-3'
            style={{ marginLeft: '-16px', marginRight: '-16px' }}
          >
            <p className='mb-0 paragraph-p3'>
              Due to technical differences in how transcription is handled, Committee events appear in this list as soon
              as they are announced, while Commons, Lords and Westminster Hall debates only appear roughly three hours
              after the debate starts.
            </p>
          </div>
        </div>
        <div className='px-4 pb-4 px-lg-5'>
          {isLoading ? (
            <SkeletonTheme baseColor='#fff'>
              {Array(10)
                .fill()
                .map((item, index) => {
                  return (
                    <div key={index} className='mb-2'>
                      <Skeleton height={19} width={`60%`} />
                    </div>
                  );
                })}
            </SkeletonTheme>
          ) : groupedItems.length === 0 ? (
            <p className='mb-0 light-60-text pt-2'>No events or debates with transcripts available</p>
          ) : (
            <>
              {groupedItems.map(({ section, items }) => (
                <div key={section} className='mb-3'>
                  <h2
                    className='title-h5 font-weight-bold specific-y-padding-12 specific-left-padding-16 specific-right-padding-16 bg-grey border-general border-grey-50 rounded briefing-section-title flex-centered justify-content-between flex-wrap mb-3'
                    style={{ marginLeft: '-16px', marginRight: '-16px' }}
                  >
                    <span className='flex-centered'>
                      <span
                        className={`icon-${SECTION_ICONS[section]} d-inline-block specific-right-padding-12 briefing-section-title-icon title-${SECTION_ICONS[section] === 'group' ? 'h5' : 'h4'}`}
                      />
                      {section}
                    </span>
                  </h2>
                  <ul className='policy-list px-0 pb-2'>
                    {items.map((item) => {
                      const startAt = item.scheduledStartDateTime;
                      return (
                        <li
                          className='my-2'
                          key={item.parliamentEventId ?? item.rollingHansardContentExternalId ?? item.id}
                        >
                          <button
                            type='button'
                            className='general-button general-link pointer w-100 text-left'
                            onClick={() => {
                              setActiveDebate(item);
                              setIsOpen(true);
                            }}
                          >
                            {item.debateTitle ?? item.title}
                            {!!startAt && <span className='light-60-text ml-2'>{time(startAt)?.formatTime()}</span>}
                            {Number.isFinite(item.contributionCount) && (
                              <span className='light-60-text ml-2'>
                                {item.contributionCount}{' '}
                                {item.contributionCount === 1 ? 'contribution' : 'contributions'}
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </>
          )}
        </div>
      </CustomScrollbar>
    </div>
  );
};

export default CreateTranscripts;
